import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useDeleteArtikel, useGetArtikels } from '@/lib/queries/content';
import { ArtikelBijlage } from '@/lib/types/content';
import { Roles, tokenContainsRole } from '@/lib/context';
import { getContentRoleLabel } from '@/lib/constants/contentRoles';
import { resolveArticleImageUrls } from '@/lib/utils/articleImages';
import { downloadBlob } from '@/lib/utils/downloadBlob';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button, Card, Divider, Modal, Spin, Tag, Tooltip, Typography, message } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import relativeTime from 'dayjs/plugin/relativeTime';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

dayjs.extend(relativeTime);

const BASE_URL = import.meta.env.VITE_CONFIG_API_URL;

export const ArticleList: React.FunctionComponent = () => {
  const { t } = useTranslation('core');
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const isManagement = location.pathname.includes('/content-management');
  const showEditButton = tokenContainsRole([Roles.Administrator]) && isManagement;
  const { data: artikels, isLoading, refetch } = useGetArtikels(isAuthenticated, isManagement);
  const { mutate: deleteArtikel } = useDeleteArtikel();
  const sortedArtikels = artikels
    ?.map(a => ({ ...a, bijlagen: a.bijlagen ?? [] }))
    .sort((a, b) => (dayjs(b.datumToegevoegd).isAfter(dayjs(a.datumToegevoegd)) ? 1 : -1));
  const getBijlageNaam = (naam: string) => naam.replace(/\.pdf$/i, '');

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Weet je zeker dat je dit artikel wilt verwijderen?',
      content: 'Deze actie kan niet ongedaan worden gemaakt.',
      okText: 'Ja, verwijderen',
      okType: 'danger',
      cancelText: 'Annuleren',
      onOk: () => {
        deleteArtikel(id, {
          onSuccess: () => {
            refetch();
          },
        });
      },
    });
  };

  const handleDownloadBijlage = async (bijlage: ArtikelBijlage) => {
    try {
      const response = await fetch(`${BASE_URL}/artikel-bijlage/${bijlage.id}`);
      if (!response.ok) {
        throw new Error(`Failed to download attachment ${bijlage.id}`);
      }

      const blob = await response.blob();
      const fileName = `${getBijlageNaam(bijlage.naam)}.pdf`;
      downloadBlob(blob, fileName);
    } catch (error) {
      console.error('Error downloading article attachment:', error);
      message.error(t('articleAttachments.downloadError'));
    }
  };

  return (
    <>
      {isLoading && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '5%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Spin size="large" />
        </div>
      )}
      {sortedArtikels?.map(artikel => (
        <div
          key={artikel.id}
          style={{
            width: '800px',
            margin: 'auto',
            marginBottom: '20px',
            position: 'relative',
          }}>
          <Card
            title={<Typography.Title level={2}>{artikel.titel}</Typography.Title>}
            extra={
              showEditButton && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Tooltip title="Bijwerken">
                    <Button
                      aria-label="Bijwerken"
                      type="text"
                      icon={<EditOutlined style={{ color: '#1677ff' }} />}
                      onClick={() => navigate(`/article/edit/${artikel.id}`)}
                    />
                  </Tooltip>
                  <Tooltip title="Verwijderen">
                    <Button aria-label="Verwijderen" type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(artikel.id)} />
                  </Tooltip>
                </div>
              )
            }>
            {isManagement && (
              <div style={{ marginBottom: 16 }}>
                {artikel.isPubliek ? (
                  <Tag color="green">Publiek</Tag>
                ) : (
                  artikel.rollen?.map(role => (
                    <Tag color="blue" key={role}>
                      {getContentRoleLabel(role)}
                    </Tag>
                  ))
                )}
              </div>
            )}
            <ReactQuill
              value={resolveArticleImageUrls(artikel.body)}
              readOnly={true}
              modules={{
                toolbar: false,
              }}
              style={{
                border: 'none',
              }}
            />
            {artikel.bijlagen.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography.Text strong>{t('articleAttachments.attachmentHeader')}</Typography.Text>
                  {artikel.bijlagen.map(bijlage => (
                    <Button key={bijlage.id} type="link" style={{ padding: 0, height: 'auto' }} onClick={() => void handleDownloadBijlage(bijlage)}>
                      {t('articleAttachments.attachmentName', { name: getBijlageNaam(bijlage.naam) })}
                    </Button>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>
      ))}
    </>
  );
};
